import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertOctagon, CheckCircle, Smartphone } from 'lucide-react';

// Base de données étendue de scénarios réels (arnaques et sûrs)
const allQuizData = [
  // ARNAQUES
  { text: "Félicitations ! Votre numéro a été tiré au sort pour gagner 500.000 FCFA de la fondation X. Cliquez ici pour réclamer.", type: "arnaque" },
  { text: "Votre compte bancaire a été bloqué suite à une activité suspecte. Vérifiez vos infos d'urgence via ce lien: http://secure-banque-update.com", type: "arnaque" },
  { text: "Dépôt Mobile Money de 150.000 FCFA reçu. Appelez urgemment le 6X XX XX XX si c'est une erreur.", type: "arnaque" },
  { text: "Urgent : J'ai eu un accident et je suis à l'hôpital. Envoie-moi vite 20.000F sur ce numéro, je te rembourse demain.", type: "arnaque" },
  { text: "Offre d'emploi : Gagnez de 30.000 à 100.000 FCFA par jour en travaillant depuis chez vous. Répondez 'OUI' sur WhatsApp.", type: "arnaque" },
  { text: "Votre colis est en attente au centre de tri. Veuillez payer les frais de douane de 1.500F ici pour valider la livraison.", type: "arnaque" },
  { text: "Avertissement WhatsApp : Votre compte expirera dans 24h. Cliquez ici pour le renouveler gratuitement.", type: "arnaque" },
  { text: "MTN/Moov Info : Vous avez souscrit à un service payant. Envoyez STOP au 8XXX ou cliquez sur le lien pour annuler.", type: "arnaque" },
  // SÛRS
  { text: "Bonjour, n'oublie pas notre réunion de demain à 10h au bureau.", type: "safe" },
  { text: "MTN/Moov : Cher client, votre forfait internet de 2Go expire demain à 23h59.", type: "safe" },
  { text: "Salut ! On se voit toujours pour le déjeuner ce midi ? Préviens-moi si tu as du retard.", type: "safe" },
  { text: "Code de sécurité : 482910. Ne partagez jamais ce code avec personne. Si vous n'avez rien demandé, ignorez ce message.", type: "safe" },
  { text: "Votre paiement de 15.000 FCFA au supermarché a été approuvé. Solde actuel : 45.000 FCFA.", type: "safe" },
  { text: "Rappel : Votre facture d'électricité est disponible. Pensez à la régler avant le 15 du mois.", type: "safe" }
];

// Fonction pour mélanger et tirer 3 cartes au hasard
function getRandomQuizzes(count = 3) {
  const shuffled = [...allQuizData].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count).map((item, index) => ({ ...item, id: Date.now() + index }));
}

export default function QuizSwipe() {
  const [cards, setCards] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [exitX, setExitX] = useState(0);

  // Initialiser les cartes au chargement
  useEffect(() => {
    setCards(getRandomQuizzes(3));
  }, []);

  const handleSwipe = (direction) => {
    const activeCard = cards[0];
    if (!activeCard) return;

    const isCorrect = (direction === 'left' && activeCard.type === 'arnaque') || (direction === 'right' && activeCard.type === 'safe');
    
    setExitX(direction === 'left' ? -300 : 300);
    setFeedback(isCorrect ? 'correct' : 'wrong');
    
    setTimeout(() => {
      setCards((prev) => prev.slice(1));
      setFeedback(null);
      setExitX(0);
    }, 400); // Wait for exit animation
  };

  if (cards.length === 0) {
    return (
      <div className="mx-4 mt-8 bg-brand-navy rounded-3xl p-6 text-center shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-accent-blue/10 animate-pulse" />
        <CheckCircle className="w-12 h-12 text-accent-blue mx-auto mb-3 relative z-10" />
        <h3 className="text-white font-bold text-lg relative z-10">Test terminé !</h3>
        <p className="text-white/70 text-sm mt-1 relative z-10">Vous avez l'œil aiguisé pour détecter les menaces.</p>
        <button onClick={() => setCards(getRandomQuizzes(3))} className="mt-4 px-6 py-2 bg-white text-brand-navy rounded-full text-sm font-bold active:scale-95 transition-transform relative z-10">Rejouer avec de nouveaux messages</button>
      </div>
    );
  }

  const activeCard = cards[0];

  return (
    <div className="mx-4 mt-8">
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-text-primary text-sm">Testez vos réflexes</p>
        <span className="text-xs font-bold bg-accent-blue/10 text-accent-blue px-2 py-1 rounded-md">{cards.length} restants</span>
      </div>
      
      <div className="relative h-48 bg-gray-100 rounded-3xl overflow-hidden flex items-center justify-center">
        {/* Indicators */}
        <div className="absolute top-1/2 left-4 -translate-y-1/2 text-red-500/30 flex flex-col items-center">
          <AlertOctagon className="w-8 h-8" />
          <span className="text-[10px] font-bold uppercase mt-1">Arnaque</span>
        </div>
        <div className="absolute top-1/2 right-4 -translate-y-1/2 text-emerald-500/30 flex flex-col items-center">
          <CheckCircle className="w-8 h-8" />
          <span className="text-[10px] font-bold uppercase mt-1">Sûr</span>
        </div>

        {/* Feedback Overlay */}
        <AnimatePresence>
          {feedback === 'correct' && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute z-30 top-4 bg-emerald-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
              Bon réflexe !
            </motion.div>
          )}
          {feedback === 'wrong' && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute z-30 top-4 bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
              Attention, c'était un piège !
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="popLayout">
          <motion.div
            key={activeCard.id}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset }) => {
              if (offset.x < -100) handleSwipe('left');
              else if (offset.x > 100) handleSwipe('right');
            }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: feedback ? 0.9 : 1, 
              opacity: 1, 
              x: feedback ? exitX : 0,
              transition: { type: "spring", stiffness: 300, damping: 20 }
            }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileDrag={{ scale: 1.05, cursor: "grabbing" }}
            className="absolute w-64 bg-white p-5 rounded-2xl shadow-xl border-t border-gray-100 cursor-grab touch-none z-20"
          >
            <div className="flex items-center gap-2 text-text-subtle mb-3">
              <Smartphone className="w-4 h-4" />
              <span className="text-xs font-medium">Nouveau message</span>
            </div>
            <p className="text-text-primary text-sm leading-relaxed font-medium">"{activeCard.text}"</p>
            <div className="mt-4 flex justify-between text-[10px] text-gray-400 font-bold uppercase tracking-widest">
              <span>← Arnaque</span>
              <span>Sûr →</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
